import { useApolloClient } from "@apollo/client";
import { useCallback, useEffect, useState } from "react";
import { REMOVE_DOMAIN_USER, REMOVE_GROUP_FROM_USER, RemoveDomainUserResponse, RemoveGroupFromUserResponse } from "../../../Services/Agrisys_GqlDataCaller/UserCalls/GQLCalls/workerListCalls";

interface RemoveUserArgs {
  username: string;
  domain: string;
  userGroupId?: number | null;
}

export default function useRemoveUser({ args }: { args: RemoveUserArgs }) {
  const client = useApolloClient();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 2000);
      return () => clearTimeout(timer);
    }

    setData(null);
    setError(null);
  }, [args, error]);

  const fetchData = useCallback(async () => {
    if (!args.domain) {
      setError("Please select a domain first");
      return null;
    }
    try {
      setLoading(true);
      setError(null);
      let result;
      
      if (args.userGroupId) { // Remove from group
        const response = await client.mutate<RemoveGroupFromUserResponse>({
          mutation: REMOVE_GROUP_FROM_USER,
          variables: { username: args.username, domain: args.domain, usergroupid: args.userGroupId},
          context: { clientName: "internal", headers: { tocloud:true } },
          fetchPolicy: "network-only",
        });
        result = response?.data?.user?.removeGroupUser ?? null;
      } else { // Remove from domain (if userGroupId is not selected)
        const response = await client.mutate<RemoveDomainUserResponse>({
          mutation: REMOVE_DOMAIN_USER,
          variables: { username: args.username, domain: args.domain },
          context: { clientName: "internal", headers: { tocloud:true } },
          fetchPolicy: "network-only",
        });
        result = response?.data?.user?.removeDomainUser ?? null;
      }

      if (!result) {
        setError("No response from server");
        setData(null);
        return null;
      } else {
        setData(result);
        return result;
      }
    } catch (err: any) {
      setError(err.message || "Unknown error");
      return null;
    } finally {
      setLoading(false);
    }
  }, [client, args]);

  return { data, loading, error, fetchData };
}